<template>
    <v-container fluid mt-0 pt-4 pr-12 pb-8 pl-8>
          <v-row>
            <v-col class="px-8">
              <template>
                <v-row>
                  <v-col>
                    <h1 class="ml-4">Profiles</h1>
                  </v-col>
                </v-row>
              </template>

              <v-card width="100%" class="mb-0" elevation="0" v-if="this.showFilters">
                <v-card-title class="pt-0 pb-0">
                  Filter By &hellip;
                </v-card-title>
                <v-card-text>
                  <template>
                    <v-row align="center" justify="center">
                      <v-col>
                        <v-select
                          ref="sel_profile_type"
                          v-model="profileTypeModel"
                          :items="columnFeatures['profile_type']"
                          :menu-props="{ maxHeight: '400' }"
                          :label="filterLabels['profile_type']"
                          multiple
                          hint="Filter profiles by selected profile types"
                          persistent-hint
                          clearable
                          dense
                          @input="updateFilteredProfiles"
                        ></v-select>
                      </v-col>
                      <v-col>
                        <v-select
                          ref="sel_items"
                          v-model="teamModel"
                          :items="columnFeatures['teams']"
                          item-text="team_name"
                          item-value="id"
                          :menu-props="{ maxHeight: '400' }"
                          :label="filterLabels['teams']"
                          multiple
                          hint="Filter profiles by selected teams"
                          persistent-hint
                          clearable
                          dense
                          return-object
                          @input="updateFilteredProfiles"
                        ></v-select>
                      </v-col>
                      <v-col cols="2">
                        <v-btn color="primary" small @click="clearFilters">Clear Filters</v-btn>
                      </v-col>
                      <v-col cols="2" class="text-right">
                        <v-btn @click="manageTeamsClick" color="primary" small>Manage Teams</v-btn>
                      </v-col>
                    </v-row>
                  </template>
                </v-card-text>
              </v-card>

              <v-card class="mt-0" elevation="0">
                <v-card-title class="pt-0">
                  <v-text-field
                    v-model="search"
                    append-icon="mdi-magnify"
                    label="Search"
                    clearable
                    single-line
                    hide-details
                  ></v-text-field>
                </v-card-title>

                <v-data-table
                  v-model="selectedProfiles"
                  :search="search"
                  :headers="agentHeaders"
                  :items="profileItems"
                  :loading="this.agentDataLoadingStatus != 'loaded'"
                  :loading-text="'Loading profiles...'"
                  :show-select="false"
                  :custom-sort="customSort"
                  class="elevation-1"
                >
                <template v-for="agentHeader in agentHeaders" v-slot:[`header.${agentHeader.value}`]="{ header }">
                  <v-tooltip v-if="agentHeader.tooltip" bottom>
                    <template v-slot:activator="{ on }">
                      <span v-on="on">{{agentHeader.text}}</span>
                    </template>
                    <span>{{ agentHeader.tooltip }}</span>
                  </v-tooltip>
                  <span v-else>{{agentHeader.text}}</span>
                </template>

                  <template v-slot:item.profile_type="{ item }">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on }">
                        <v-icon v-on="on">{{ getProfileClass(item.profile_type, item.groupid) }}</v-icon>
                      </template>
                      <div class="multiLineTooltip">{{ getProfileTitle(item) }}</div>
                    </v-tooltip>
                  </template>
                  <template v-slot:item.name="{ item }">
                    <span :title="item.id">{{ item.name }}</span>
                  </template>
                  <template v-slot:item.email="{ item }">
                    <inline-email-editor v-if="item.profile_type == 0" :origEmail="item.email" :id="item.id" @update-agent="updateProfile($event)" class="editable" title="Click to edit email"></inline-email-editor>
                    <span v-else>{{item.email}}</span>
                  </template>
                  <template v-slot:item.last_login_date="{ item }">
                    <span :title="item.last_login_date">{{formatDate(item.last_login_date)}}</span>
                  </template>
                  <template v-slot:item.teamButton="{ item }" class="text-right">
                    <span v-if="teamNameString(item).length <= MAX_TEAM_NAME_DISPLAY_LENGTH">{{ teamNameString(item) }}</span>
                    <template v-else-if="formatTeamNames(item).includes('…')">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                          <span v-on="on">{{ formatTeamNames(item) }}</span>
                        </template>
                        <span>{{ teamNameString(item) }}</span>
                      </v-tooltip>
                    </template>
                    <template v-else>
                      <span>{{ formatTeamNames(item) }}</span>
                    </template>
                  </template>
                  <template v-slot:item.reassign_tasks="{ item }">
                      <v-icon
                      v-if="item.profile_type == 1"
                      small
                      class="ml-2"
                      style="cursor:pointer"
                      title="Edit Teams"
                      @click="reassignTaskOwnership(item)"
                      >
                      mdi-clipboard-plus-outline
                      </v-icon>
                  </template>
                </v-data-table>
              </v-card>
            </v-col>
          </v-row>
          <div>
          <manage-teams
            :showManageTeamsModal="showManageTeams"
            @HIDE_MANAGE_TEAMS_MODAL="hideManageTeamsModal"
          ></manage-teams>
          <reassign-task-ownership
            :showReassignTaskModal="showReassignTaskModal"
            :profileId="selectedProfileId"
            :profileType="selectedProfileType"
            :users="getUsers"
            :profileTaskAndGuidanceSessionCounts="getUserTaskAndGuidanceSessionCounts(selectedProfileId)"
            @HIDE_REASSIGN_TASK_MODAL="hideReassignTaskOwnershipModal"
          ></reassign-task-ownership>
        </div>
    </v-container>
</template>
<script>
    import {mapActions, mapGetters} from "vuex"
    import InlineEmailEditor from "@/components/Widgets/InlineEmailEditor.vue"
    import ManageTeams from "@/components/Setup/ManageTeams.vue"
    import ReassignTaskOwnership from "@/components/Setup/ReassignTaskOwnership.vue"
    import moment from "moment"

    const ADMIN_USER_GROUP_IDS = [35, 41] // Example group IDs for admin users, adjust as necessary

    export default {
    name: "AgentSetup",
    props: {
    },
    components: {
      InlineEmailEditor,
      ManageTeams,
      ReassignTaskOwnership
    },

    data() {
      return {
        agentHeaders: [
          { text: 'Type', value: 'profile_type', width: '3%' },
          { text: 'Agent/Username', value: 'name', width: '18%' },
          { text: 'Email', value: 'email', width: '25%', tooltip: 'Email updates can be made to agents exclusively. If a user requires an email change, please contact your analyst.'},
          { text: 'Name', value: 'full_name', width: '11%'},
          { text: 'Last Login', value: 'last_login_date', width: '8%'},
          { text: 'Last 30-Day Login Count', value: 'login_count', width: '12%', tooltip: 'Count of Logins in last 30 days'},
          { text: 'Call Access Count', value: 'listen_count', width: '12%'},
          { text: 'Teams', value: 'teamButton', width: '8%'},
          { text: 'Reassign Task Ownership', value: 'reassign_tasks', width: '1%', sortable: false, tooltip: 'Reassign all tasks owned by this profile to another profile' }
        ],
        profileItems: [],
        selectedProfiles: [],
        selectedProfileId: null,
        selectedProfileType: null,
        filteredProfileItems: [],
        search: '',
        showFilters: false,
        profileTypeModel: [],
        teamModel: [],
        showTeamEditModal: false,
        showManageTeams: false,
        showReassignTaskModal: false,
        profileTeams: null,
        columnFeatures: {
          profile_type: ['Agent', 'User'],
          teams: []
        },
        filterLabels: {
          profile_type: 'User Type',
          teams: 'Team'
        },
        MAX_TEAM_NAME_DISPLAY_LENGTH: 25,
        NO_TEAM_TEXT: 'Unassigned',
      }
    },
    methods: {
      ...mapActions("resources", [
        "retrieveAgentData",
        "retrieveTeamData",
        "updateAgentEmail"
      ]),
      ...mapActions("taskSummaryData", ["retrieveUserTasksAndGuidanceSessionCounts"]),
      async getTaskAndGuidanceSessionCounts() {
        // Ensure task and guidance session counts are loaded
        const payload = {
          user_ids: this.agentData
            .filter(profile => profile.profile_type === 1)
            .map(profile => profile.id)
        }
        await this.retrieveUserTasksAndGuidanceSessionCounts(payload)
      },
      customSort(items, index, isDesc) {
        let xIndex = (Array.isArray(index)) ? index[0] : index
        let xIsDesc = (Array.isArray(isDesc)) ? isDesc[0] : isDesc

        items.sort((a, b) => {
          if (xIndex === "login_count" || xIndex === "last_login_date") {
            if (!xIsDesc) {
              if (!a[xIndex] && a[xIndex]!=0) return -1
              else return a[xIndex] < b[xIndex] ? -1 : 1
            } else {
              if (!b[xIndex] && b[xIndex]!=0) return -1
              else return b[xIndex] < a[xIndex] ? -1 : 1
            }
          } else if (xIndex === "profile_type") {
            if (!xIsDesc) {
              if (a[xIndex] == 0 && b[xIndex] != 0) return -1
              else if (!ADMIN_USER_GROUP_IDS.includes(a['groupid']) && ADMIN_USER_GROUP_IDS.includes(b['groupid'])) return -1
              else return 1
            } else {
              if (b[xIndex] == 0 && a[xIndex] != 0) return -1
              else if (!ADMIN_USER_GROUP_IDS.includes(b['groupid']) && ADMIN_USER_GROUP_IDS.includes(a['groupid'])) return -1
              else return 1
            }
          } else if (xIndex === "teamButton") {
            // sort by team names formatted, treating "Unassigned" as the lowest priority
            let aTeams = this.formatTeamNames(a).toLowerCase()
            let bTeams = this.formatTeamNames(b).toLowerCase()
            if (aTeams === this.NO_TEAM_TEXT.toLowerCase()) aTeams = 'zzzzzzzzzzzzzzzzzzzzzzzzzzz' // ensure "Unassigned" sorts last
            if (bTeams === this.NO_TEAM_TEXT.toLowerCase()) bTeams = 'zzzzzzzzzzzzzzzzzzzzzzzzzzz' // ensure "Unassigned" sorts last
            if (!xIsDesc) {
              return aTeams.localeCompare(bTeams)
            } else {
              return bTeams.localeCompare(aTeams)
            }
          } else {
            if (!xIsDesc) {
              return a[xIndex] < b[xIndex] ? -1 : 1;
            } else {
              return b[xIndex] < a[xIndex] ? -1 : 1;
            }
          }
        });
        return items;
      },
      formatDate(time) {
        let date = moment(time)
        return (date.isValid() ? date.format("M/D/YY") : '')
      },
      updateProfile(payload) {
        this.updateAgentEmail(payload)
      },
      getProfileClass(profile_type, groupid) {
        return (profile_type == 0 ? 'mdi-headset' :
          ADMIN_USER_GROUP_IDS.includes(groupid) ? 'mdi-account-star' : 'mdi-account')
      },
      getProfileTitle(profile) {
        let profile_title = ''
        if (profile.profile_type == 0) {
          profile_title = 'Agent'
        } else if (profile.profile_type == 1) {
          if (ADMIN_USER_GROUP_IDS.includes(profile.groupid)) {
            profile_title = 'Administrator'
          } else {
            profile_title = 'Standard'
          }
          if (profile.settings) {
            profile_title += "\nCall Export " + (profile.settings.transcriptExports ? 'Enabled' : 'Disabled')
            profile_title += "\nManual Call Scorecard Updates " + (profile.settings.manualScoring ? 'Enabled' : 'Disabled')
          }
        }
        return profile_title
      },
      reassignTaskOwnership(item) {
        this.$nextTick(() => {
          this.selectedProfileId = item.id
          this.selectedProfileType = item.profile_type
          this.showReassignTaskModal = true
        })
      },
      manageTeamsClick() {
        this.showManageTeams = true
      },
      loadData() {
        this.retrieveAgentData({}).then(() => {
          if (this.teamDataLoadingStatus != 'loaded') {
            this.retrieveTeamData()
          }
          this.buildTeamFilter()
        }).catch((error) => {
          console.error('Error loading agent data:', error)
        })

      },
      loadPage() {
        if (this.agentDataLoadingStatus == 'loaded') {
          // establish data table and enable filtering
          this.profileItems = this.sortedProfileItems
          this.showFilters = true
          this.updateFilteredProfiles()
          this.getTaskAndGuidanceSessionCounts()
        }
      },
      buildTeamFilter() {
        if (this.teamDataLoadingStatus == 'loaded') {
          this.columnFeatures.teams = this.sortedTeams
        }
      },
      clearFilters() {
        this.profileTypeModel = []
        this.teamModel = []
        this.updateFilteredProfiles()
      },
      updateFilteredProfiles() {
        this.filteredProfileItems = []
        this.sortedProfileItems.forEach((profile) => {
          // loop through profiles and match on profile and/or team
          let typeMatch = false
          let teamMatch = false
          if (this.profileTypeModel.length > 0) {
            typeMatch = (this.profileTypeModel.includes('Agent') && profile.profile_type == 0) || (this.profileTypeModel.includes('User') && profile.profile_type == 1)
          } else {
            // no type(s) selected
            typeMatch = true
          }
          if (this.teamModel.length > 0) {
            let profile_team_ids = profile.teams.map(team => team.id)
            let selected_team_ids = this.teamModel.map(team => team.id)
            teamMatch = profile_team_ids.some(id => selected_team_ids.includes(id))

            // if teamModel is Unassigned, also match all profiles with no teams
            if (this.teamModel.some(team => team.id === null)) {
              teamMatch = teamMatch || profile.teams.length === 0
            }
          } else {
            // no team(s) selected
            teamMatch = true
          }
          if (typeMatch && teamMatch) this.filteredProfileItems.push(profile)
        })
        this.profileItems = (this.profileTypeModel.length > 0 || this.teamModel.length > 0) ? this.filteredProfileItems : this.sortedProfileItems
      },
      hideModal(payload) {
        this.showTeamEditModal = false
        if (payload.reload) {
          this.loadData()
        }
      },
      hideManageTeamsModal(payload) {
        this.showManageTeams = false
        if (payload.reload) {
          this.loadData()
        }
      },
      hideReassignTaskOwnershipModal(payload) {
        this.showReassignTaskModal = false
        if (payload.reload) {
          this.getTaskAndGuidanceSessionCounts()
        }
      },
      teamNameString(profile) {
        return profile.teams.map(team => team.team_name).sort((a, b) => a.localeCompare(b)).join(', ') || this.NO_TEAM_TEXT
      },
      formatTeamNames(profile) {
        const maxLength = this.MAX_TEAM_NAME_DISPLAY_LENGTH
        const names = profile.teams.map(team => team.team_name).sort((a, b) => a.localeCompare(b))

        // If no names, return 'Unassigned'
        if (!names.length) return this.NO_TEAM_TEXT
        // If only one name, return it
        if (names.length === 1) return names[0]

        // Build up the output one name at a time until we exceed maxLength
        let output = ''
        let usedLength = 0
        let count = 0

        for (let i = 0; i < names.length; i++) {
          const name = names[i]
          const separator = i > 0 ? ', ' : ''
          const nextChunk = separator + name
          const nextLen = usedLength + nextChunk.length

          // If we can still append without exceeding maxLength, do so
          if (nextLen <= maxLength) {
            output += nextChunk
            usedLength = nextLen
            count++
          } else {
            // We can’t fit the next name: break and compute remaining
            break
          }
        }

        // Determine if we need to add an ellipsis and count of remaining names
        // If we reached the end of the list, no need for ellipsis
        const remaining = names.length - count
        if (remaining > 0) {
          // Reserve space for ellipsis and “+N”
          const suffix = `…+${remaining}`
          // If even the current output plus suffix is too long, trim it
          if (usedLength + suffix.length > maxLength) {
            // Chop output to fit suffix exactly
            const allowed = maxLength - suffix.length
            output = output.slice(0, allowed).replace(/,?\s*$/, '')
          }
          output += suffix
        }
        return output
      },
      getUserTaskAndGuidanceSessionCounts(profileId) {
        return this.taskAndGuidanceSessionCounts[profileId] || {}
      },
    },
    computed: {
      ...mapGetters("resources", [
        'agentData',
        'agentDataLoadingStatus',
        'agentDataLoadingError',
        'agentDataLoadingErrorStatus',

        'teamData',
        'teamDataLoadingStatus',
        'teamDataLoadingError',
        'teamDataLoadingErrorStatus',
      ]),
      ...mapGetters("taskSummaryData", ["taskAndGuidanceSessionCounts", "taskAndGuidanceSessionCountsLoadingStatus"]),
      sortedProfileItems() {
        return [...this.agentData].sort((a, b) => a.name.localeCompare(b.name))
      },
      sortedTeams() {
        // Copy and sort teams by name, including "Unassigned"
        const teams = [...this.teamData]
        teams.push({ id: null, team_name: this.NO_TEAM_TEXT }) // Add "Unassigned" team
        // Sort, ensuring "Unassigned" is sorted alphabetically
        return teams.sort((a, b) => a.team_name.localeCompare(b.team_name))
      },
      getUsers() {
        // Filter agentData to return only users (profile_type == 1)
        return this.agentData && this.agentData.length > 0 ? this.agentData.filter(agent => agent.profile_type === 1) : []
      },
    },
    watch: {
      agentDataLoadingStatus: 'loadPage',
      teamDataLoadingStatus: 'buildTeamFilter'
    },
    mounted() {
      this.loadData()
    },
  };
  </script>

<style scoped>
  .editable { cursor: pointer; }
  .multiLineTooltip { white-space: pre-line; }
</style>
